import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './MobileGalleryPreview.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import videoPlaceholder from '../../../assets/images/placeholder-video-thumb.svg';
import {
  getSubtypeQueryValue,
  normalizeMediaItem as normalizeGalleryMediaItem,
  fetchAlbumMediaItems,
  generateVideoThumbnail,
} from './galleryUtils';

const CACHE_TTL_MS = 60 * 1000;

const MobileGalleryPreview = ({
  rows = 3,
  columns = 5,
  albumId = 'all',
  subtype = 'all',
  onOpenGallery,
  className = '',
  instanceId = 'global',
}) => {
  const { get } = useApi();
  const { t, language } = useLanguage();
  const [media, setMedia] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [videoThumbnailsLoading, setVideoThumbnailsLoading] = useState({});
  const cacheRef = useRef(new Map());
  const previewLimit = Math.max(1, rows * columns);

  const translateOrFallback = useCallback((key, fallback, options) => {
    if (typeof t !== 'function') {
      return fallback;
    }
    const value = t(key, options);
    if (!value || value === key) {
      return fallback;
    }
    return value;
  }, [t]);

  const numberFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it-IT');
    } catch (error) {
      return new Intl.NumberFormat('it-IT');
    }
  }, [language]);

  const normalizeMediaItem = useCallback((item) => {
    return normalizeGalleryMediaItem(item, t);
  }, [t]);

  const resolveMediaSourceUrl = useCallback((item) => {
    if (!item) {
      return null;
    }
    const rawSource = item.raw || {};
    return (
      rawSource.public_url ||
      rawSource.url ||
      rawSource.file_url ||
      rawSource.fileUrl ||
      item.url ||
      null
    );
  }, []);

  const resolveThumbnailKey = useCallback((item) => {
    const rawKey = item?.id || resolveMediaSourceUrl(item);
    return rawKey ? `${rawKey}` : null;
  }, [resolveMediaSourceUrl]);

  const handleVideoMetadata = useCallback((event) => {
    const video = event.currentTarget;
    if (!video || typeof video.currentTime !== 'number') {
      return;
    }
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = duration > 0 ? Math.min(0.1, Math.max(0, duration - 0.1)) : 0.1;
    if (Number.isFinite(targetTime) && targetTime > 0 && video.currentTime !== targetTime) {
      try {
        video.currentTime = targetTime;
      } catch (error) {
        // Ignore seek failures for thumbnails.
      }
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    const cacheKey = `${instanceId || 'global'}|${albumId || 'all'}|${subtype || 'all'}`;
    const now = Date.now();
    const cached = cacheRef.current.get(cacheKey);

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      setMedia(cached.media);
      setTotalItems(cached.totalItems);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let mediaItems = [];
      let total = 0;

      if (albumId && albumId !== 'all') {
        const albumMediaItems = await fetchAlbumMediaItems({ get, albumId });
        let normalizedAlbumMedia = albumMediaItems
          .map((item) => normalizeMediaItem(item))
          .filter(Boolean);

        if (subtype && subtype !== 'all') {
          normalizedAlbumMedia = normalizedAlbumMedia.filter((item) => item.type === subtype);
        }

        mediaItems = normalizedAlbumMedia.slice(0, previewLimit);
        total = normalizedAlbumMedia.length;
      } else {
        const params = new URLSearchParams({
          page: '1',
          page_size: String(previewLimit),
        });

        const subtypeValue = getSubtypeQueryValue(subtype);
        if (subtypeValue) {
          params.set('subtype', subtypeValue);
        }

        const response = await get(`/api/v1/media/files?${params.toString()}`);
        const items = Array.isArray(response?.items) ? response.items : [];
        const normalized = items
          .map((entry) => normalizeMediaItem(entry?.media_file || entry?.media || entry))
          .filter(Boolean);

        mediaItems = normalized.slice(0, previewLimit);
        const paginationInfo = response?.pagination || {};
        total = paginationInfo.total_items || paginationInfo.totalItems || normalized.length;
      }

      setMedia(mediaItems);
      setTotalItems(total);
      cacheRef.current.set(cacheKey, {
        media: mediaItems,
        totalItems: total,
        timestamp: now,
      });
    } catch (err) {
      console.error('[MobileGalleryPreview] Failed to load preview', err);
      setMedia([]);
      setTotalItems(0);
      setError(err?.message || translateOrFallback('imageGallery.states.error.defaultMessage', 'Impossibile caricare la galleria'));
    } finally {
      setIsLoading(false);
    }
  }, [albumId, get, instanceId, normalizeMediaItem, previewLimit, subtype, translateOrFallback]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  useEffect(() => {
    let isCancelled = false;
    const candidates = media.filter((item) => item?.mediaType === 'video');
    if (candidates.length === 0) {
      return () => {
        isCancelled = true;
      };
    }

    const generateThumbnails = async () => {
      for (const item of candidates) {
        if (isCancelled) {
          return;
        }
        const key = resolveThumbnailKey(item);
        if (!key || videoThumbnails[key] || videoThumbnailsLoading[key]) {
          continue;
        }

        setVideoThumbnailsLoading((prev) => ({ ...prev, [key]: true }));
        const sourceUrl = resolveMediaSourceUrl(item);
        if (!sourceUrl) {
          setVideoThumbnailsLoading((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          continue;
        }

        try {
          const thumbnail = await generateVideoThumbnail(sourceUrl, { width: 200, quality: 0.8 });
          if (!isCancelled && thumbnail) {
            setVideoThumbnails((prev) => ({ ...prev, [key]: thumbnail }));
          }
        } catch (err) {
          if (!isCancelled) {
            console.error('[MobileGalleryPreview] Failed to generate video thumbnail', err);
          }
        } finally {
          if (!isCancelled) {
            setVideoThumbnailsLoading((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }
        }
      }
    };

    generateThumbnails();

    return () => {
      isCancelled = true;
    };
  }, [media, resolveMediaSourceUrl, resolveThumbnailKey, videoThumbnails, videoThumbnailsLoading]);

  const remainingCount = Math.max(0, (totalItems || 0) - media.length);

  const handleOpen = useCallback(() => {
    if (typeof onOpenGallery === 'function') {
      onOpenGallery();
    }
  }, [onOpenGallery]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  }, [handleOpen]);

  const placeholders = useMemo(() => {
    if (!isLoading) {
      return Math.max(0, previewLimit - media.length);
    }
    return previewLimit;
  }, [isLoading, media.length, previewLimit]);

  const tileElements = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: previewLimit }).map((_, index) => (
        <div key={`skeleton-${index}`} className="mobile-gallery-preview-tile skeleton" />
      ));
    }

    const tiles = media.map((item, index) => {
      const isLastVisible = index === Math.min(media.length, previewLimit) - 1;
      const showMoreOverlay = isLastVisible && remainingCount > 0;
      const isVideo = item?.mediaType === 'video' || item?.raw?.media_type === 'video';
      const thumbnailKey = resolveThumbnailKey(item);
      const thumbnailUrl = thumbnailKey ? videoThumbnails[thumbnailKey] : null;

      return (
        <div key={item.id || index} className="mobile-gallery-preview-tile">
          {isVideo ? (
            thumbnailUrl ? (
              <img src={thumbnailUrl} alt={item.name} loading="lazy" />
            ) : (
              <video
                src={item.url || ''}
                muted
                playsInline
                preload="auto"
                poster={videoPlaceholder}
                onLoadedMetadata={handleVideoMetadata}
              />
            )
          ) : (
            <img src={item.url || ''} alt={item.name} loading="lazy" />
          )}
          {showMoreOverlay && (
            <div className="mobile-gallery-preview-tile-overlay">
              <span>+{numberFormatter.format(remainingCount)}</span>
            </div>
          )}
        </div>
      );
    });

    if (placeholders > 0) {
      const filler = Array.from({ length: placeholders }).map((_, index) => (
        <div key={`placeholder-${index}`} className="mobile-gallery-preview-tile placeholder" />
      ));
      return [...tiles, ...filler];
    }

    return tiles;
  }, [
    handleVideoMetadata,
    isLoading,
    media,
    numberFormatter,
    placeholders,
    previewLimit,
    remainingCount,
    resolveThumbnailKey,
    translateOrFallback,
    videoThumbnails,
  ]);

  const hasContent = media.length > 0;

  const titleLabel = translateOrFallback('aiStorage.galleryPreview.title', 'Galleria');
  const emptyLabel = translateOrFallback('aiStorage.galleryPreview.empty', 'Ancora nessun media salvato');
  const retryLabel = translateOrFallback('common.actions.retry', 'Riprova');

  return (
    <div
      className={['mobile-gallery-preview-card', className].filter(Boolean).join(' ')}
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      aria-label={titleLabel}
    >
      <div className="mobile-gallery-preview-header">
        <div className="mobile-gallery-preview-copy">
          <p>{titleLabel}</p>
        </div>
      </div>

      <div
        className="mobile-gallery-preview-grid"
        style={{ '--gallery-preview-columns': String(columns) }}
      >
        {tileElements}
      </div>

      {error && (
        <div className="mobile-gallery-preview-status error">
          <p>{error}</p>
          <button type="button" onClick={(event) => { event.stopPropagation(); fetchPreview(); }}>
            {retryLabel}
          </button>
        </div>
      )}

      {!error && !isLoading && !hasContent && (
        <div className="mobile-gallery-preview-status empty">
          <p>{emptyLabel}</p>
        </div>
      )}
    </div>
  );
};

export default MobileGalleryPreview;
